import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  Clock, 
  CheckCircle, 
  HelpCircle,
  Share2,
  ThumbsUp,
  ThumbsDown
} from 'lucide-react';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';
import axios from 'axios';



const GroupVoting = () => {
  const { groupId } = useParams();
  const [group, setGroup] = useState(null);
  const [votingResults, setVotingResults] = useState({});
  const [userVotes, setUserVotes] = useState({});
  const [socket, setSocket] = useState(null);
  const [userId] = useState(`user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [isLoading, setIsLoading] = useState(true);

  const fetchGroupDetails = useCallback(async () => {
    try {
      const response = await axios.get(`/api/groups/${groupId}`);
      if (response.data.success) {
        setGroup(response.data.group);
        setVotingResults(response.data.votingResults);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Fetch group error:', error);
      toast.error('Failed to load group details');
    }
  }, [groupId]);

  useEffect(() => {
    // Initialize Socket.IO connection
    const newSocket = io('http://localhost:5001');
    setSocket(newSocket);

    // Join the group room
    newSocket.emit('join-group', groupId);

    // Listen for vote updates
    newSocket.on('vote-update', (data) => {
      console.log('Vote update received:', data);
      // Update voting results when someone votes
      fetchGroupDetails();
    });

    // Listen for plan updates
    newSocket.on('plan-updated', (data) => {
      console.log('Plan update received:', data);
      fetchGroupDetails();
    });

    return () => {
      newSocket.disconnect();
    };
  }, [groupId, fetchGroupDetails]);

  useEffect(() => {
    fetchGroupDetails();
  }, [fetchGroupDetails]);

  const submitVote = async (planId, vote) => {
    try {
      const response = await axios.post(`/api/groups/${groupId}/vote`, {
        userId,
        planId,
        vote
      });

      if (response.data.success) {
        setVotingResults(response.data.votingResults);
        setUserVotes(prev => ({
          ...prev,
          [planId]: vote
        }));
        
        // Emit vote to other users
        socket?.emit('vote', {
          groupId,
          userId,
          planId,
          vote
        });

        toast.success('Vote submitted!');
      }
    } catch (error) {
      console.error('Submit vote error:', error);
      toast.error('Failed to submit vote');
    }
  };

  const finalizePlan = async (selectedPlanId) => {
    try {
      const response = await axios.post(`/api/groups/${groupId}/finalize`, {
        selectedPlanId
      });

      if (response.data.success) {
        setGroup(response.data.group);
        toast.success('Plan finalized!');
      }
    } catch (error) {
      console.error('Finalize plan error:', error);
      toast.error('Failed to finalize plan');
    }
  };

  const shareGroup = () => {
    const groupUrl = `${window.location.origin}/group/${groupId}`;
    navigator.clipboard.writeText(groupUrl);
    toast.success('Group link copied to clipboard!');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading group...</p>
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Group Not Found</h2>
        <p className="text-gray-600">The group you're looking for doesn't exist.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* Group Header */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{group.name}</h1>
              <p className="text-gray-600">Group ID: {groupId}</p>
            </div>
            <button
              onClick={shareGroup}
              className="btn-secondary flex items-center space-x-2"
            >
              <Share2 size={16} />
              <span>Share</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <Users size={16} className="text-gray-400" />
              <span>{group.members.length} members</span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock size={16} className="text-gray-400" />
              <span>Created {new Date(group.createdAt).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 rounded-full text-xs ${
                group.status === 'voting' ? 'bg-yellow-100 text-yellow-800' :
                group.status === 'decided' ? 'bg-green-100 text-green-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {group.status.charAt(0).toUpperCase() + group.status.slice(1)}
              </span>
            </div>
          </div>
        </div>

        {/* Voting Section */}
        {group.status === 'voting' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card"
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Vote on Plans
            </h2>

            <div className="space-y-4">
              {group.planOptions.map((plan, index) => {
                const planVotes = votingResults[plan.id] || { yes: 0, no: 0, maybe: 0, total: 0 };
                const userVote = userVotes[plan.id];
                const totalVotes = planVotes.total;
                const yesPercentage = totalVotes > 0 ? (planVotes.yes / totalVotes) * 100 : 0;

                return (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index }}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {plan.title}
                        </h3>
                        <p className="text-gray-600 mb-2">{plan.description}</p>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span>Cost: {plan.estimatedCost}</span>
                          <span>Duration: {plan.duration}</span>
                        </div>
                        {plan.studentPerks && plan.studentPerks.length > 0 && (
                          <div className="mt-2 text-xs text-green-600">
                            🎓 {plan.studentPerks[0]}
                          </div>
                        )}
                      </div>

                      {/* Vote Buttons */}
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => submitVote(plan.id, 'yes')}
                          className={`p-2 rounded-lg transition-colors ${
                            userVote === 'yes'
                              ? 'bg-green-100 text-green-700 border border-green-300'
                              : 'bg-gray-100 text-gray-600 hover:bg-green-50'
                          }`}
                          title="Vote Yes"
                        >
                          <ThumbsUp size={16} />
                        </button>
                        <button
                          onClick={() => submitVote(plan.id, 'maybe')}
                          className={`p-2 rounded-lg transition-colors ${
                            userVote === 'maybe'
                              ? 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                              : 'bg-gray-100 text-gray-600 hover:bg-yellow-50'
                          }`}
                          title="Maybe"
                        >
                          <HelpCircle size={16} />
                        </button>
                        <button
                          onClick={() => submitVote(plan.id, 'no')}
                          className={`p-2 rounded-lg transition-colors ${
                            userVote === 'no'
                              ? 'bg-red-100 text-red-700 border border-red-300'
                              : 'bg-gray-100 text-gray-600 hover:bg-red-50'
                          }`}
                          title="Vote No"
                        >
                          <ThumbsDown size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Voting Results */}
                    {totalVotes > 0 && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                          <span>Votes: {totalVotes}</span>
                          <span>{yesPercentage.toFixed(0)}% Yes</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${yesPercentage}%` }}
                          ></div>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
                          <span>Yes: {planVotes.yes}</span>
                          <span>Maybe: {planVotes.maybe}</span>
                          <span>No: {planVotes.no}</span>
                        </div>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>

            {/* Finalize Button */}
            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  // Find the plan with the most yes votes
                  const bestPlan = group.planOptions.reduce((best, plan) => {
                    const votes = votingResults[plan.id] || { yes: 0 };
                    const bestVotes = votingResults[best?.id] || { yes: 0 };
                    return votes.yes > bestVotes.yes ? plan : best;
                  }, null);

                  if (bestPlan) {
                    finalizePlan(bestPlan.id);
                  }
                }}
                className="btn-primary"
              >
                Finalize Best Plan
              </button>
            </div>
          </motion.div>
        )}

        {/* Decided Plan */}
        {group.status === 'decided' && group.decidedPlan && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card bg-green-50 border-green-200"
          >
            <div className="flex items-center space-x-2 mb-4">
              <CheckCircle size={20} className="text-green-600" />
              <h2 className="text-xl font-semibold text-gray-900">
                Plan Finalized!
              </h2>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {group.decidedPlan.title}
              </h3>
              <p className="text-gray-600">{group.decidedPlan.description}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Cost:</span> {group.decidedPlan.estimatedCost}
                </div>
                <div>
                  <span className="font-medium">Duration:</span> {group.decidedPlan.duration}
                </div>
              </div>

              {group.decidedPlan.itinerary && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-900 mb-2">Itinerary:</h4>
                  <div className="space-y-2">
                    {group.decidedPlan.itinerary.map((item, index) => (
                      <div key={index} className="text-sm">
                        <div className="font-medium">{item.time} - {item.activity}</div>
                        <div className="text-gray-600">{item.venue}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default GroupVoting; 